import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicAvailabilityResponse } from '@crm/shared';
import { PublicLayout } from './PublicLayout';
import { DatePickerPopover } from './DatePickerPopover';
import {
  fetchAvailability,
  fetchBookingByToken,
  publicErrorMessage,
  rescheduleByToken,
} from './api';

function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ReschedulePage() {
  const { t } = useTranslation();
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const qc = useQueryClient();

  const [date, setDate] = useState<string>(todayISO());
  const [done, setDone] = useState(false);

  const booking = useQuery({
    queryKey: ['public', 'booking', token],
    queryFn: () => fetchBookingByToken(token),
    enabled: token.length > 0,
    retry: false,
  });

  const serviceIds = booking.data?.services.map((s) => s.serviceId) ?? [];
  const employeeId = booking.data?.employee.id;

  const availability = useQuery<PublicAvailabilityResponse>({
    queryKey: ['public', 'availability-for-reschedule', token, date],
    queryFn: () =>
      fetchAvailability({
        // Reschedule trzyma usługi i pracownika — jednak `availability`
        // jest po stronie BE liczone od bazowych durations w Service.
        // To szacunkowe sloty; finalna walidacja przy POST /reschedule.
        serviceIds: booking.data!.services.map((s) => s.serviceId),
        employeeId: booking.data!.employee.id,
        date,
      }),
    enabled: !!booking.data && date.length === 10 && serviceIds.length > 0 && !!employeeId,
  });

  const mutation = useMutation({
    mutationFn: (startAt: string) => rescheduleByToken(token, { startAt }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['public', 'booking', token] });
      setDone(true);
    },
  });

  if (done) {
    return (
      <PublicLayout>
        <div className="rounded-lg bg-emerald-50 p-6 ring-1 ring-emerald-300 dark:bg-emerald-950/40 dark:ring-emerald-800">
          <h1 className="text-2xl font-semibold text-emerald-900 dark:text-emerald-200">
            {t('publicBooking.reschedule.successTitle')}
          </h1>
          <p className="mt-1 text-sm text-emerald-800 dark:text-emerald-300">
            {t('publicBooking.reschedule.successSubtitle')}
          </p>
          <div className="mt-4">
            <Link
              to={`/rezerwacja/anuluj/${token}`}
              className="inline-flex rounded-md bg-white px-3 py-2 text-sm text-zinc-700 ring-1 ring-zinc-300 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
            >
              {t('publicBooking.success.linkCancel')}
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (booking.isLoading) {
    return (
      <PublicLayout>
        <p className="dark:text-zinc-300">{t('publicBooking.loading')}</p>
      </PublicLayout>
    );
  }

  if (booking.error || !booking.data) {
    return (
      <PublicLayout>
        <div className="rounded-lg bg-zinc-50 p-6 ring-1 ring-zinc-200 dark:bg-zinc-900/50 dark:ring-zinc-800">
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
            {t('publicBooking.cancel.alreadyTitle')}
          </h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            {t('publicBooking.cancel.alreadySubtitle')}
          </p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="space-y-4">
        <header>
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
            {t('publicBooking.reschedule.title')}
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.reschedule.subtitle')}</p>
        </header>

        <div className="rounded-lg bg-brand-50 p-4 ring-1 ring-brand-200 dark:bg-brand-900/30 dark:ring-brand-700">
          <p className="text-xs uppercase tracking-wide text-brand-700 dark:text-brand-300">
            {t('publicBooking.reschedule.currentLabel')}
          </p>
          <p className="mt-1 text-sm font-medium text-brand-900 dark:text-brand-100">
            {formatDateTime(booking.data.startAt)} ·{' '}
            {booking.data.employee.firstName} {booking.data.employee.lastName}
          </p>
        </div>

        <DatePickerPopover
          value={date}
          onChange={setDate}
          label={t('publicBooking.slot.datePicker')}
        />

        {availability.isLoading ? <p className="dark:text-zinc-300">{t('publicBooking.loading')}</p> : null}
        {availability.error ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
            {publicErrorMessage(availability.error, '')}
          </p>
        ) : null}

        {availability.data && availability.data.slots.length === 0 ? (
          <p className="rounded-lg bg-white p-4 text-zinc-500 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-400 dark:ring-zinc-800">
            {t('publicBooking.slot.noSlots')}
          </p>
        ) : null}

        {availability.data && availability.data.slots.length > 0 ? (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
            {availability.data.slots.map((s) => (
              <button
                key={s.startAt}
                type="button"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate(s.startAt)}
                className="rounded-md bg-white px-3 py-2 text-sm font-medium text-zinc-800 ring-1 ring-zinc-200 transition hover:border-brand-500 hover:ring-brand-400 disabled:opacity-50 dark:bg-zinc-900 dark:text-zinc-100 dark:ring-zinc-700 dark:hover:ring-brand-400"
              >
                {formatTime(s.startAt)}
              </button>
            ))}
          </div>
        ) : null}

        {mutation.isError ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
            {publicErrorMessage(mutation.error, t('publicBooking.errorGeneric'))}
          </p>
        ) : null}

        <div className="flex justify-between">
          <Link
            to="/rezerwacja"
            className="rounded-lg bg-white px-4 py-2 text-sm text-zinc-700 ring-1 ring-zinc-300 transition hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
          >
            {t('publicBooking.back')}
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
